<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-ca-main">
    <div class="ca-options">
      <div class="ca-tablebox-container">
        <TableBox
          title="Creditors List"
          :tableData.sync="creditorsList"
          :selectedRow.sync="selectedCreditors"
          :maxSelection="-1"
          :loading="tableLoading"
          :showSelectAll="true"
          :dataType="{ '#': 'number', 'Last Update Time': 'datetime', 'Create Time': 'datetime' }"
        />
      </div>
      <div style="margin-top: 5px">
        <div>
          <BoxContainer class="date-options-container" style="width: auto; margin-left: 0px; margin-bottom: 5px">
            <div style="display: column">
              <DateTimePicker
                placeholder="Date"
                :value.sync="dateValue"
                :errorMessage.sync="dateErr"
                :isRequired="true"
                :showTime="false"
              />

              <div style="display: flex; align-items: center; gap: 24px">
                <CheckBox title="Include Current Month" :isChecked="currMonthTicked" />

                <CheckBox title="Show Zero Balance" :isChecked.sync="zeroBalTicked" />

                <CheckBox title="By Currency" :isChecked.sync="byCurrencyTicked" />
              </div>
            </div>
          </BoxContainer>
        </div>
      </div>

      <div style="display: flex; flex-direction: row; justify-content: space-between; margin-top: 5px">
        <Dropdown
          placeholder="Print Format"
          :items="printFormatItems"
          itemKey="label"
          :selectedObject.sync="selectedPrintFormat"
          :isRequired="true"
          :errorMessage.sync="printFormatErr"
          style="width: 50%"
        />
        <button @click="previewPrintFormat">Preview Creditor Ageing</button>
      </div>
    </div>
    <div class="ca-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="printDetails.reportFiles"
        :reportData="printDetails.reportDetails"
        :sharedData="printDetails.sharedDetails"
        :canToggleBackground="false"
        :canShare="false"
        :canClose="false"
        :downloadName="`Creditor Ageing ${selectedPrintFormat.label} ${new Date().formatString('yyyyMMddHHmm')}`"
        :shareBehaviour="printDetails.designerV3ShareBehaviour"
      />
      <div v-else class="user-instructions">
        <span>Click on <strong>Preview Creditor Ageing</strong> to get started</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {},
  data: function () {
    return {
      creditorsList: [],
      selectedCreditors: [],

      dateValue: null,
      dateErr: '',

      zeroBalTicked: false,
      currMonthTicked: false,
      byCurrencyTicked: false,

      tableLoading: true,

      printFormatItems: [
        { label: 'Summary (Prepay)', prepay: true },
        { label: 'Summary', prepay: false },
        { label: 'Details (Prepay)', prepay: true },
        { label: 'Details', prepay: false },
      ],

      userDetails: null,

      selectedPrintFormat: null,
      printFormatErr: '',

      showDesignerV3: false,
      printDetails: {
        sharedDetails: {
          companyName: null,
          pageNumber: '{{jsreportPageNum}} of {{jsreportTotalPage}}',
          ageingDate: null,
          printDate: null,
        },
        reportDetails: [], // Empty for now, look at previewFormatStatement for what each object looks like for printing
        designerV3ShareBehaviour: {
          behaviour: 'uploadAndEmit',
          options: {
            fileName: '',
            folder: '',
          },
        },
        reportFiles: null,
      },
    };
  },
  props: {},
  methods: {
    async previewPrintFormat() {
      if (this.selectedCreditors.length == 0) {
        this.$showAlert('error', `No Creditors selected`, `Please select at least one Creditor to proceed`);
        return;
      }

      if (this.selectedPrintFormat === null) {
        this.printFormatErr = 'Missing field';
        return;
      }

      this.printDetails.sharedDetails.ageingDate = this.dateValue.toLocaleDateString('en-CA');
      this.printDetails.sharedDetails.printDate = new Date().toLocaleDateString('en-CA');

      this.$fullscreenLoading('Previewing Creditor Ageing');
      this.printFormatErr = '';
      const rptTypeMap = {
        'Summary (Prepay)': 'AGEING_SUMMARY_PREPAY',
        Summary: 'AGEING_SUMMARY',
        'Details (Prepay)': 'AGEING_DETAILS_PREPAY',
        Details: 'AGEING_DETAILS',
      };
      const jsonBody = {
        //OffsetFmt, postpay, curmth in SP doesn't do much, clrtbl set to 1 in C#, might think for TmpTblNaming
        creditorarray: this.selectedCreditors.map((item) => item['Creditor Code']).join(','),
        strdate: this.printDetails.sharedDetails.ageingDate,
        chkcurmon: this.currMonthTicked ? '1' : '0',
        chkshowzerocreditor: this.zeroBalTicked ? '1' : '0',
        prepay: this.selectedPrintFormat?.prepay ? '1' : '0',
        tmptblnaming: `${this.userDetails.LoginUserId}_${new Date().formatString('yyyyMMdd')}`,
        rpttype: rptTypeMap[this.selectedPrintFormat.label],
        chkcurrency: this.byCurrencyTicked ? '1' : '0',
        cosname: this.userDetails.CoRefname,
      };
      const finalJSON = await this.$apiCallV2('/CreditorAgeing/GetAPAgingProc', jsonBody);

      this.formatPrintJson(finalJSON);
      // console.log(formatPrin)
      if (this.selectedPrintFormat.label.includes('Prepay')) {
        this.printDetails.reportFiles = Array(finalJSON.length).fill('PrintCreditorAgeingPrepay.json');
      } else {
        this.printDetails.reportFiles = Array(finalJSON.length).fill('PrintCreditorAgeing.json');
      }

      this.printDetails.reportDetails = finalJSON;
      this.showDesignerV3 = true;
      this.$fullscreenLoading();
    },
    formatAgeing(item, depth) {
      const depthLabel = depth !== null ? `depth${depth}` : '';
      item.Ageing.forEach((value, index) => {
        item[`Ageing${index}${depthLabel}`] = this.formatAmount(value);
      });

      delete item.Ageing;

      return item;
    },
    formatAmount: function (amount) { // Original is from SOA by Chloe
      const strAmt = amount.toString();
      let formattedAmt = '';
      if (Number(amount) < 0) {
        formattedAmt = `(${this.$currencyFormat(strAmt.replaceAll(',', '')).replace('-', '')})`;
      } else {
        formattedAmt = this.$currencyFormat(strAmt.replaceAll(',', ''));
      }
      return formattedAmt;
    },
    formatCurrencyFormat(rawKey, result) {
      const keyName = rawKey.split('depth')[0];

      const currencyKeys = new Set([
        'RunningBalance',
        'Balance',
        'fAgeing0',
        'fCurPay',
        'advPay',
        'overPay',
        'OpeningBalance'
      ]);

      if (currencyKeys.has(keyName)) {
        result[rawKey] = this.formatAmount(result[rawKey]);
      }
    },
    formatPrintJson(result) {
      const isSummary = this.selectedPrintFormat.label.includes('Summary');

      result.forEach((currency) => {
        Object.keys(currency).forEach((key) => {
          this.formatCurrencyFormat(key, currency);
        });

        this.formatAgeing(currency, null);

        currency.Accounts.forEach((account) => {
          Object.keys(account).forEach((key) => {
            this.formatCurrencyFormat(key, account);
          });

          this.formatAgeing(account, 0);

          account.Payees.forEach((payee) => {
            Object.keys(payee).forEach((key) => {
              this.formatCurrencyFormat(key, payee);
            });

            this.formatAgeing(payee, 1);

            if (isSummary) {
              payee.Details = [];
            } else {
              payee.Details.forEach((detail) => {
                Object.keys(detail).forEach((key) => {
                  this.formatCurrencyFormat(key, detail);
                });

                this.formatAgeing(detail, 2);
              });
            }
          });
        });
      });
    },
  },
  async mounted() {
    this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CREDITOR' }).then((result) => {
      this.creditorsList = result;
      this.tableLoading = false;
    });
    this.userDetails = await this.$apiCallV2('/Login/GetDetails');
    this.printDetails.sharedDetails.companyName = this.userDetails.CoName;
    this.dateValue = new Date();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.print-ca-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .ca-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;
    width: 50%;
    height: 100%;
    display: flex;
    flex-direction: column;

    > .ca-tablebox-container {
      margin-top: 5px;
      //height: 45%;
      flex: 1;
      overflow-y: auto;
    }

    .date-options-container {
      width: 50%;
      margin-left: 10px;
      height: 80%;
      align-self: center;
    }
  }

  > .ca-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }
}
</style>
